import { Injectable } from '@angular/core';

import { MainService } from 'src/app/app.service';

/** Role global do ESP. No Smart Review vale como god-mode. */
/**
 * Cópia LOCAL da regra de permissão do Smart Review.
 *
 * A autoridade é o backend, que a devolve já resolvida em `GET /me/capabilities` — o mesmo
 * endpoint que este app já chama para saber as origens disponíveis, e cujo campo
 * `capabilities` ele lê e não usa.
 *
 * A cópia existe porque as roles chegam no login (`POST /api/v1/tokens`) e ficam em
 * sessionStorage: derivar daqui responde sem uma ida ao servidor, e é o que gateia rota e
 * botão. As duas derivações concordam hoje. Quem mudar a regra — capability nova, ou uma
 * passando a implicar outra — muda em três lugares: aqui, no backend e na extensão do
 * VS Code, que prefere a resposta do servidor e só cai na cópia dela quando não o alcança.
 */
const SUPERUSER = 'superuser';

/**
 * Capabilities somáveis do Smart Review, entregues no array `roles` do login
 * (`POST /api/v1/tokens` → `MainService.sRoles`). São independentes entre si:
 * `admin` não implica `run`, `audit` não implica `run`. Qualquer uma delas dá
 * acesso à ferramenta.
 *
 * `member` é o tier base: além de ler, inclui e remove feedback e sai do próprio
 * review — por isso não se chama `read`.
 */
export const SmartReviewRole = {
  MEMBER: 'smartreview.member',
  RUN: 'smartreview.run',
  AUDIT: 'smartreview.audit',
  ADMIN: 'smartreview.admin'
} as const;

/**
 * Fonte única de permissão do Smart Review no front — único lugar que conhece os
 * literais das roles. Lê `MainService.sRoles` (array completo de roles), e não
 * `userToken.role`, que é derivado e só assume `superuser|team|user`.
 *
 * `superuser` (role global do ESP) é a única role cumulativa: concede todas as
 * operações. As `smartreview.*` seguem granulares.
 *
 * O front usa isto apenas para adequar a UI: a autoridade continua sendo o backend,
 * que responde 403 nas rotas sem a capability necessária.
 */
@Injectable({
  providedIn: 'root'
})
export class SmartReviewPermissionsService {
  constructor(private mainService: MainService) {}

  /** Qualquer `smartreview.*` — ou `superuser` — dá acesso à ferramenta. */
  hasToolAccess(): boolean {
    return this.isSuperuser() || Object.values(SmartReviewRole).some(role => this.has(role));
  }

  /** Disparar/reprocessar reviews, gerar prompts e rodar o assistant. */
  hasRun(): boolean {
    return this.isSuperuser() || this.has(SmartReviewRole.RUN);
  }

  /** Visão global (reviews de todas as áreas/usuários), read-only. */
  hasAudit(): boolean {
    return this.isSuperuser() || this.has(SmartReviewRole.AUDIT);
  }

  /**
   * Visão global: admin ou audit (superuser vale pelos dois) — a mesma regra do backend,
   * decisão 0030. Vê os reviews de todo mundo, e abre inclusive o review que terminou com
   * erro (ver `reviewOpenBlock`).
   */
  hasGlobalView(): boolean {
    return this.hasAdmin() || this.hasAudit();
  }

  /** `/admin/*` (gates, curadoria) e gerenciamento de feedback/reviews alheios. */
  hasAdmin(): boolean {
    return this.isSuperuser() || this.has(SmartReviewRole.ADMIN);
  }

  private isSuperuser(): boolean {
    return this.has(SUPERUSER);
  }

  private has(role: string): boolean {
    return this.roles().includes(role);
  }

  /**
   * `sRoles` é populado no login e re-hidratado por `validSession()`. Num reload
   * direto numa rota do módulo, o guard de autenticação já hidratou o estado; o
   * fallback cobre a ordem inversa sem forçar leitura de sessionStorage aqui.
   */
  private roles(): string[] {
    if (!this.mainService.sRoles?.length) {
      this.mainService.validSession();
    }

    return (this.mainService.sRoles ?? [])
      .filter(role => typeof role === 'string')
      .map(role => role.trim().toLowerCase());
  }
}
